import { clubFeaturesOf } from 'utils/clubFeatures';

// What an athlete's competition group is called.
//
// Every athlete in a club has one group number. What the number means depends
// on the club's grouping model. There are three, named by what they are, and
// a club's model is chosen when it is created and never changes. The examples
// are for a child whose competing age is 10 (the age on 31 December of the
// season's start year) and who is in Year 5:
//
//   Under:  the "under" band from the birth date. The child is 11, written
//           U11 in a heading. Every club made before October 2026. The
//           band runs to U20 (a competing age of 19); from 20 the number
//           is the age itself.
//   Age:    the competing age itself, with no "U" anywhere. The child is 10.
//   Year:   the school year level plus 5. The child is 10, shown as Y5.
//           (Schools only. The year before Year 1 is level 0, stored as 5.)
//
// A table cell shows the bare number for Under and for Age, so most screens
// are right for both without knowing which it is. What differs is a heading
// (formatGroupHeading) and a range (formatGroupRange).
//
// Ranges and codes are always STORED in the "U" form ("U10-U11" is numbers 10
// to 11), for every model. A year club's admin types and sees years
// ("Y5-Y6") and an Age club's sees ages ("10-11"); the server converts what
// is typed, and this file converts what is shown.
//
// This is the website's copy of the rules in the API's
// Services/Calculation/AthleteGrouping.cs, and groupLabel.test.js holds it to
// the same table of cases. Nothing outside this file should build a "U" or
// "Y" label itself.

// How a club groups its athletes. The numbers are the API's GroupingBasis.
// UNDER was named AGE until October 2026, when the Age model was added; its
// number has not changed, and every club that existed then has it.
export const GROUPING_BASIS = { UNDER: 0, YEAR_LEVEL: 1, AGE: 2 };

const YEAR_LEVEL_OFFSET = 5;
const MIN_YEAR_LEVEL = 0;
const MAX_YEAR_LEVEL = 12;

// The grouping model of a club object from the clubs list. A club with no
// value (an older API, or no club selected) is treated as an Under club, as
// is any value this file does not know.
export const groupingBasisOf = (club) => {
  if (club?.groupingBasis === GROUPING_BASIS.YEAR_LEVEL) {
    return GROUPING_BASIS.YEAR_LEVEL;
  }
  return club?.groupingBasis === GROUPING_BASIS.AGE
    ? GROUPING_BASIS.AGE
    : GROUPING_BASIS.UNDER;
};

const isYearLevel = (basis) => basis === GROUPING_BASIS.YEAR_LEVEL;
const isAge = (basis) => basis === GROUPING_BASIS.AGE;

// ---- Who is placed by age ----
//
// A result is placed within its group number, or, for an adult, only in its
// own race. Where that line falls depends on the organisation. This is the
// website's copy of the API's AthleteGrouping.OldestNumberPlacedByAge, and
// nothing else in the website may hold the limit as a number of its own.
//
//   An organisation with no adult athletes (a school): no limit.
//   A club on the Age model: 19.
//   A club on the Under model: 20, which is U20. A 20 year old shares that
//   number and is placed with them (Doug, 2026-10-14).

// The last competing age in the Under model's band: 19 is stored as 20 and
// shown as U20. From the next age the stored number is the age itself.
export const UNDER_BAND_LAST_AGE = 19;

// The oldest competing age a club places by age. Junior athletics ends at
// Under 20.
const OLDEST_AGE_PLACED_BY_AGE_IN_A_CLUB = 19;

// The age from which a club's athlete is an adult.
export const FIRST_ADULT_AGE = OLDEST_AGE_PLACED_BY_AGE_IN_A_CLUB + 1;

// The highest group number the club places by age. Infinity for a school.
export const oldestGroupPlacedByAge = (club) => {
  if (!clubFeaturesOf(club).adultAthletes) return Infinity;
  return isAge(groupingBasisOf(club))
    ? OLDEST_AGE_PLACED_BY_AGE_IN_A_CLUB
    : OLDEST_AGE_PLACED_BY_AGE_IN_A_CLUB + 1;
};

// Whether a group number is placed by age, given the club's limit
// (oldestGroupPlacedByAge). No number, or 0, is not: such a result has no age.
export const isPlacedByAge = (groupNumber, oldestPlacedByAge) => {
  const number = Number(groupNumber);
  return Number.isFinite(number) && number > 0 && number <= oldestPlacedByAge;
};

// The words a club reads for its grouping (Doug, 2026-10-11). The two kinds
// of club get completely separate words, everywhere a user reads them: a club
// that groups by age sees Age and Age Group, a club that groups by year level
// sees Year and Year Group. The words follow how the club groups its
// athletes, not whether it is a school. Under and Age are both "by age" and
// read the same words.
//
//   value, values   one athlete's own value: a column heading or a filter
//   group, groups   a competition group, and the settings page that defines them
//   view            the results view
//
// Nothing outside this file should choose between "Age" and "Year" itself, so
// that a word can be changed here once. Another set of words (a "Grade" for
// American schools) would be added here.
//
// Database columns, API field names and the iPad contract keep their "age"
// names; only text a user reads goes through these.
const titleWords = (value, values) => ({
  value,
  values,
  group: `${value} Group`,
  groups: `${value} Groups`,
  view: `${value} View`,
});

const mapWords = (words, change) =>
  Object.fromEntries(
    Object.entries(words).map(([key, word]) => [key, change(word)])
  );

const withOtherCases = (words) => ({
  ...words,
  // The same words inside a sentence: "age group", "years".
  lower: mapWords(words, (word) => word.toLowerCase()),
  // The same words starting a sentence: "Age group", "Year groups".
  sentence: mapWords(
    words,
    (word) => word.charAt(0) + word.slice(1).toLowerCase()
  ),
});

const AGE_WORDS = withOtherCases(titleWords('Age', 'Ages'));
const YEAR_WORDS = withOtherCases(titleWords('Year', 'Years'));

export const groupWords = (basis) =>
  isYearLevel(basis) ? YEAR_WORDS : AGE_WORDS;

// The heading of a column that holds one athlete's value when its rows can come
// from clubs of both kinds: a parent's own page lists their athletes in every
// club they belong to. `bases` is the grouping basis of each row. All age clubs
// reads "Age", all year-level clubs reads "Year", a mix reads "Age / Year", and
// no rows at all reads "Age", as the page always has.
export const valueHeadingFor = (bases) => {
  const hasYear = bases.some((basis) => isYearLevel(basis));
  const hasAge = bases.some((basis) => !isYearLevel(basis));
  if (hasYear && hasAge) return `${AGE_WORDS.value} / ${YEAR_WORDS.value}`;
  return hasYear ? YEAR_WORDS.value : AGE_WORDS.value;
};

// Sentences that differ in kind between the two sorts of club, not only in a
// word (Doug, 2026-10-11, inventory Q3 and Q6). An age club works each
// athlete's competing age out from a date, so a season has an age calculation
// date and a new season recalculates ages. A year-level club has no such date:
// a new season moves every student up one year level.
//
//   hasAgeCalculationDate  whether the season screens show that date at all
//   seasonUpdated          the count shown when a new season has started
//   seasonUpdating         what the server is doing while it starts
//   seasonRollover         what starting a season does to each athlete
//   seasonChat             what it does to the automatic chat groups
//   hoursPerAthlete        how volunteer hours are set when they follow the athlete
const AGE_PHRASES = {
  hasAgeCalculationDate: true,
  seasonUpdated: 'Competing ages updated',
  seasonUpdating: 'updating athlete ages',
  seasonRollover:
    'Competing ages for all club athletes are recalculated for the new season',
  seasonChat:
    "Age-group chat memberships are changed using the new season's athlete ages",
  hoursPerAthlete: 'Set volunteer hours for each competing age.',
};

const YEAR_PHRASES = {
  hasAgeCalculationDate: false,
  seasonUpdated: 'Year levels updated',
  seasonUpdating: 'updating year levels',
  seasonRollover: 'Every athlete moves up one year level for the new season',
  seasonChat:
    "Year-group chat memberships are changed using the new season's year levels",
  hoursPerAthlete: 'Set volunteer hours for each year level.',
};

export const groupPhrases = (basis) =>
  isYearLevel(basis) ? YEAR_PHRASES : AGE_PHRASES;

const isValidYearLevel = (yearLevel) =>
  Number.isInteger(yearLevel) &&
  yearLevel >= MIN_YEAR_LEVEL &&
  yearLevel <= MAX_YEAR_LEVEL;

// The year level a stored group number stands for, or null when the number is
// not one a year club can hold (for example 0 on a walk-up guest's result).
const yearLevelFromGroup = (groupNumber) => {
  const yearLevel = Number(groupNumber) - YEAR_LEVEL_OFFSET;
  return isValidYearLevel(yearLevel) ? yearLevel : null;
};

// The year levels a student can be in, as the options of a dropdown: Y0 to
// Y12. The values are text, as a form holds them ('' is "not chosen yet").
export const YEAR_LEVEL_OPTIONS = Array.from(
  { length: MAX_YEAR_LEVEL - MIN_YEAR_LEVEL + 1 },
  (unused, index) => {
    const yearLevel = MIN_YEAR_LEVEL + index;
    return { value: String(yearLevel), label: `Y${yearLevel}` };
  }
);

// A student's own year level, as text for a form, from their stored group
// number (10 gives '5'). '' when the number is not a year level, so the form
// shows the field as not chosen.
export const yearLevelOfGroup = (groupNumber) => {
  const yearLevel = yearLevelFromGroup(groupNumber);
  return yearLevel === null ? '' : String(yearLevel);
};

// The bare number that follows the club's word for one athlete's value:
// "Age 11" for an age club, "Year 5" for a year club (never "Year Y5"). The
// word itself is groupWords(basis).value.
export const formatGroupNumber = (basis, groupNumber) => {
  const yearLevel = isYearLevel(basis) ? yearLevelFromGroup(groupNumber) : null;
  return String(yearLevel === null ? groupNumber : yearLevel);
};

// ---- A group that arrives as a name or as a number ----
//
// The results list shows a meet's events in two views. The event view carries
// the group's NAME, as the official picked it ("U10", or "Y5" for a year
// club; it can also be a word such as "Mixed"). The age view carries the bare
// group NUMBER ("10"). The two helpers below let one screen handle both.

// The key a group goes by, so a name and a number that mean the same group
// match: "U10" and "10" give "10" for an age club; "Y5" and "10" give "10" for
// a year club (Year 5 is group 10). Anything else is its own key.
export const groupKeyOfName = (basis, name) => {
  const text = String(name ?? '').trim();
  if (!isYearLevel(basis)) return text.replace(/^U/i, '');
  const match = /^Y(\d{1,2})$/i.exec(text);
  return match && isValidYearLevel(Number(match[1]))
    ? String(Number(match[1]) + YEAR_LEVEL_OFFSET)
    : text;
};

// What to show for it: a bare number as the club reads one athlete's group
// (formatGroupCell: "10", or "Y5" for a year club); a name as it is.
export const formatGroupName = (basis, value) => {
  const text = String(value ?? '').trim();
  return /^\d+$/.test(text) ? formatGroupCell(basis, Number(text)) : text;
};

// One athlete's group in a table cell, badge or chip.
// Age club: the bare number (11, 42). That is deliberate: an adult's number
// is their age and cannot be turned into "Senior" or "Masters" reliably.
// Year club: "Y6".
export const formatGroupCell = (basis, groupNumber) => {
  const yearLevel = isYearLevel(basis) ? yearLevelFromGroup(groupNumber) : null;
  return yearLevel === null ? String(groupNumber) : `Y${yearLevel}`;
};

// A group in a heading, column title or filter option: "U11" for an Under
// club, "Y6" for a year club, and the number alone, "10", for an Age club
// (Doug, 2026-10-13: a heading reads as a cell does, with no "U").
export const formatGroupHeading = (basis, groupNumber) => {
  if (isAge(basis)) return String(groupNumber);
  const yearLevel = isYearLevel(basis) ? yearLevelFromGroup(groupNumber) : null;
  return yearLevel === null ? `U${groupNumber}` : `Y${yearLevel}`;
};

// An athlete's age or year for a season, shown beside their name on the athlete
// results pages.
// Under club: the competing age, with the age group in brackets while the athlete
// is in the band, which runs to U20: "9(U10)", "19(U20)". From 20 there is no
// age group, so the age alone: "20".
// Age club: the competing age alone, "9": the group is that same number.
// Year club: the year, "Y5". Its competing age is not an age anyone should read.
// Nothing to show gives ''.
export const formatAthleteSeasonGroup = (basis, competingAge, ageGroup) => {
  if (isYearLevel(basis)) {
    return ageGroup ? formatGroupHeading(basis, ageGroup) : '';
  }
  if (competingAge == null) return '';
  if (isAge(basis)) return String(competingAge);
  return competingAge <= UNDER_BAND_LAST_AGE
    ? `${competingAge}(${formatGroupHeading(basis, ageGroup)})`
    : String(competingAge);
};

// A stored range or code ("U10", "U10-U11", "U5,U7-U9") as the club reads it.
// Under: unchanged. Year: "Y5", "Y5-Y6", "Y0,Y2-Y4"; a number that is not a
// year level is left as it is. Age: the numbers with no "U": "10", "10-11",
// "5,7-9". Text with no number in it ("Senior") is returned as it is.
// For a year club apply it to stored text only: text already shown in years
// must not be passed through again.
export const formatGroupRange = (basis, storedRange) => {
  if (!storedRange) return storedRange;
  if (isAge(basis)) {
    return String(storedRange).replace(/[Uu]?(\d{1,2})/g, '$1');
  }
  if (!isYearLevel(basis)) return storedRange;
  return String(storedRange).replace(/[Uu]?(\d{1,2})/g, (match, digits) => {
    const yearLevel = yearLevelFromGroup(Number(digits));
    return yearLevel === null ? match : `Y${yearLevel}`;
  });
};

// ---- Combined groups ("Combine ages", step 8) ----
//
// The server keys a combined group's age view page by its bare numbers: "14-17",
// "8-10,12". These read it as the club reads a range, and give the group its
// heading: its name, and its range when the name is different.

// "14-17" as the club reads a range: "U14-U17" (Under), "14-17" (Age),
// "Y11-Y12" for the year club key "16-17".
export const formatGroupKey = (basis, key) =>
  formatGroupRange(basis, String(key ?? '').replace(/(\d{1,2})/g, 'U$1'));

// A combined group by name and range: "Junior (8-10)", "Year 11/12 (Y11-Y12)".
// A name that already reads as the range ("U14-U17") is not repeated, and a
// group with no name reads as its range.
export const formatCombinedGroup = (basis, name, key) => {
  const range = formatGroupKey(basis, key);
  const text = String(name ?? '').trim();
  return !text || text.toLowerCase() === range.toLowerCase()
    ? range
    : `${text} (${range})`;
};

// The group an age view page shows, from the key it was asked for by: one
// number as a heading reads it ("U11", "11", "Y6"), a combined group by name
// and range. groupName is the server's name for a combined group, if it sent one.
export const formatAgeViewGroup = (basis, pageKey, groupName) =>
  /^\d+$/.test(String(pageKey ?? '').trim())
    ? formatGroupHeading(basis, Number(pageKey))
    : formatCombinedGroup(basis, groupName, pageKey);

// Turns what a year club's admin types ("Y5", "Y5-Y6", "5-6", "Y0, Y2-Y4")
// into the stored form ("U10", "U10-U11", "U5,U7-U9"), or null when it is not
// a year range. Every year must be 0 to 12 and a range must not run
// backwards. A "U" prefix is refused: in a year club it would be ambiguous.
// The server applies the same rule when it saves; this is for checking the
// input before it is sent.
export const parseYearRange = (input) => {
  if (typeof input !== 'string' || !input.trim()) return null;

  const storedSegments = [];
  const segments = input.split(',').map((segment) => segment.trim());
  for (const segment of segments) {
    const match = /^[Yy]?(\d{1,2})(?:-[Yy]?(\d{1,2}))?$/.exec(segment);
    if (!match) return null;

    const from = Number(match[1]);
    if (!isValidYearLevel(from)) return null;

    if (match[2] === undefined) {
      storedSegments.push(`U${from + YEAR_LEVEL_OFFSET}`);
      continue;
    }

    const to = Number(match[2]);
    if (!isValidYearLevel(to) || to < from) return null;
    storedSegments.push(
      `U${from + YEAR_LEVEL_OFFSET}-U${to + YEAR_LEVEL_OFFSET}`
    );
  }
  return storedSegments.join(',');
};

// Turns what an Age club's admin types ("10", "10-11", "8-10, 12") into the
// stored form ("U10", "U10-U11", "U8-U10,U12"), or null when it is not a
// range of ages. Every number must be 1 to 99 and a range must not run
// backwards. A "U" is refused: in an Age club "U11" could be read as 10 or as
// 11, and what is typed is always the competing age itself. The server
// applies the same rule when it saves; this is for checking the input before
// it is sent.
export const parseAgeRange = (input) => {
  if (typeof input !== 'string' || !input.trim()) return null;

  const storedSegments = [];
  const segments = input.split(',').map((segment) => segment.trim());
  for (const segment of segments) {
    const match = /^(\d{1,2})(?:-(\d{1,2}))?$/.exec(segment);
    if (!match) return null;

    const from = Number(match[1]);
    if (from < 1) return null;

    if (match[2] === undefined) {
      storedSegments.push(`U${from}`);
      continue;
    }

    const to = Number(match[2]);
    if (to < from) return null;
    storedSegments.push(`U${from}-U${to}`);
  }
  return storedSegments.join(',');
};

// Where a group comes in a list of a club's groups, from its name or from its
// stored code. It is the same rule for every club.
//
// A group is placed by the first number in it: "U9" before "U11", "9" before
// "11", and "U14-U17" with the 14s. Seniors and then Masters follow every
// number, and any other name with no number ("Junior") comes last. A year
// club's groups come in year order either way they are written: as the stored
// code ("U10") or as the name ("Y5").
//
// The result is only for comparing two groups, by subtracting one from the
// other in a sort. The volunteer screens order their group sections with it.
// Until October 2026 each of them kept a table of "U5" to "U13", "U14-U17",
// "U18", "U20", Seniors and Masters, and listed any other group ("U15",
// "U13-U17") after Masters.
const SENIORS_ORDER = 1000;
const MASTERS_ORDER = 1001;
const NO_NUMBER_ORDER = 1002;

export const groupOrderOfName = (name) => {
  const text = String(name ?? '').trim();
  if (text === 'Seniors') return SENIORS_ORDER;
  if (text === 'Masters') return MASTERS_ORDER;
  const match = /\d+/.exec(text);
  return match ? Number(match[0]) : NO_NUMBER_ORDER;
};

// Where a group comes when its stored range is known: by the range, so a group
// named with a word ("Junior", stored "U8-U10") comes with the 8s and not last.
// With no range it falls back to the name. The Volunteer Management page orders
// the group filter and the role list under Sign Up Members with it, as its
// group sections are ordered by the range (step 8).
export const groupOrderOfRange = (storedRange, name) =>
  groupOrderOfName(storedRange || name);
